import { FilterCounts } from '../types/filters';
import { ReportTestItem } from '../utils/entities';
import { elementKeyExtractors } from './convertTestsToElementFacets';
import { exclusionKeyExtractors } from './convertTestsToExclusionFacets';
import { TestCounts } from './entities';
import { FilterState } from './facets';

// export type CalculatedFilters = {
//   tests: TestCounts;
// } & {
//   [key in FilterKeys]: Record<string, number>;
// };

export type CalculatedFilters = FilterCounts;
export interface Count {
  total: number;
  filtered: number;
  unmatchedSegments: number;
  missingData: number;
  excluded: number;
  status: number;
}

type CountTypes = keyof Count;

export const defaultCounts: Record<keyof Count, number> = {
  total: 0,
  filtered: 0,
  unmatchedSegments: 0,
  missingData: 0,
  excluded: 0,
  status: 0,
};

export const defaultTestCounts: TestCounts = {
  total: 0,
  published: 0,
  qa: 0,
  draft: 0,
  archived: 0,
  missingData: 0,
  fastFill: 0,
  unmatchedSegments: 0,
  manuallyExcluded: 0,
  included: 0,
  status: 0,
};

export const defaultValues = {
  tests: { ...defaultTestCounts },
  filtered: { ...defaultTestCounts },
  status: {},
  rating: {},
  testSuite: {},
  space: {},
  elementName: {},
  elementId: {},
  element: {},
  excluded: {},
};

const incrementCounts = (
  counts: Record<string, Count>,
  key: keyof Count | string,
  item: ReportTestItem,
  isFiltered: boolean
) => {
  if (!counts[key]) {
    counts[key] = { ...defaultCounts };
  }

  if (isFiltered) {
    counts[key].filtered++;

    // Independent counting: count each error type separately
    if (item.hasMissingData) {
      counts[key].missingData++;
    }

    if (item.hasUnmatchedSegments && item.switches?.fast_fill_accepted !== true) {
      counts[key].unmatchedSegments++;
    }

    if (item.status?.toLowerCase() !== 'published') {
      counts[key].status++;
    }

    if (item.switches?.include !== true) {
      counts[key].excluded++;
    }

  } else {
    counts[key].total++;
  }
};

export const generateNestedCounts = (
  filteredItems: ReportTestItem[],
  allItems: ReportTestItem[],
  keyExtractors: KeyExtractor[]
): Record<string, Count> => {
  const counts: Record<string, Count> = {};

  const processItems = (items: ReportTestItem[], isFiltered: boolean) => {
    items.forEach((item) => {
      let currentKey = '';

      // Build and process hierarchical keys
      for (const extractor of keyExtractors) {
        const value = extractor(item);
        if (value === null) break;

        currentKey = currentKey? `${currentKey}.${value}` : value;
        incrementCounts(counts, currentKey as CountTypes, item, isFiltered);
      }
    });
  };

  processItems(allItems, false);
  processItems(filteredItems, true);

  return counts;
};

export const countSimpleFilter = (
  filteredItems: ReportTestItem[],
  allItems: ReportTestItem[],
  key: keyof ReportTestItem
): Record<string, Count> => {
  const counts: Record<string, Count> = {};

  const processItems = (items: ReportTestItem[], isFiltered: boolean) => {
    items.forEach((item) => {
      const value = item[key] ? item[key]?.toString().toLowerCase() : 'null';
      if (value) {
        incrementCounts(counts, value as CountTypes, item, isFiltered);
      }
    });
  };

  processItems(allItems, false);
  processItems(filteredItems, true);

  return counts;
};

const calculateTestCounts = (items: ReportTestItem[]): TestCounts => {
  return items.reduce(
    (counts: TestCounts, item) => {
      // Increment total
      counts.total++;

      // Increment status-based counts
      const status = item.status.toLowerCase();
      switch (status) {
        case 'published':
          counts.published++;
          break;
        case 'qa':
          counts.qa++;
          break;
        case 'draft':
          counts.draft++;
          break;
        case 'archived':
          counts.archived++;
          break;
      }

      // Independent counting: each error type counted separately
      if (item.hasMissingData) {
        counts.missingData++;
      }

      if (item.hasUnmatchedSegments && item.switches?.fast_fill_accepted !== true) {
        counts.unmatchedSegments++;
        counts.fastFill++;
      }

      if (item.status?.toLowerCase() !== 'published') {
        counts.status++;
      }

      // Non-error tallies (preserved for compatibility)
      if (item.exclusionReason === 'manuallyexcluded') {
        counts.manuallyExcluded++;
      }

      if (item.exclusionReason === 'included') {
        counts.included++;
      }

      return counts;
    },
    {
      ...defaultTestCounts
    }
  );
};

export const calculateExclusionCounts = (
  filteredItems: ReportTestItem[],
  allItems: ReportTestItem[]
): Record<string, Count> => {
  const counts: Record<string, Count> = {};

  const processItems = (items: ReportTestItem[], isFiltered: boolean) => {
    items.forEach((item) => {
      const status = item.status.toLowerCase();

      // Independent counting: same logic as incrementCounts
      if (item.hasMissingData) {
        incrementCounts(counts, 'missingData', item, isFiltered);
      }

      if (item.hasUnmatchedSegments && item.switches?.fast_fill_accepted !== true) {
        incrementCounts(counts, 'unmatchedSegments', item, isFiltered);
      }

      if (item.status?.toLowerCase() !== 'published') {
        incrementCounts(counts, 'status', item, isFiltered);
      }

      if (item.switches?.include !== true) {
        incrementCounts(counts, 'excluded', item, isFiltered);
        incrementCounts(counts, 'manuallyExcluded', item, isFiltered);
      }

      if (item.exclusionReason === 'included') {
        incrementCounts(counts, 'included', item, isFiltered);
      }

      // Status-specific counts
      if (status === 'draft') {
        incrementCounts(counts, 'status.draft', item, isFiltered);
      }
      if (status === 'qa') {
        incrementCounts(counts, 'status.qa', item, isFiltered);
      }
      if (status === 'archived') {
        incrementCounts(counts, 'status.archived', item, isFiltered);
      }
    });
  };

  processItems(allItems, false);
  processItems(filteredItems, true);

  return counts;
};

export type KeyExtractor = (item: ReportTestItem) => string | null;

// Example key extractors for space hierarchy
const spaceKeyExtractors: KeyExtractor[] = [
  (item) => item.assetId?.toString() ?? 'null',
  (item) => (item.buildingId ? `${item.buildingId}` : 'null'),
  (item) => (item.levelId ? `${item.levelId}` : 'null'),
  (item) => (item.areaId ? `${item.areaId}` : 'null'),
];

// Example key extractors for rating hierarchy
const ratingKeyExtractors: KeyExtractor[] = [
  (item) => item.testSpecification.testSuiteId.toString(),
  (item) => (item.result ? `${item.result}` : 'null'),
];

// Custom count generation for test suites that handles both compliance and non-compliance hierarchies
const generateTestSuiteCounts = (
  filteredItems: ReportTestItem[],
  allItems: ReportTestItem[]
): Record<string, Count> => {
  const counts: Record<string, Count> = {};

  const processItems = (items: ReportTestItem[], isFiltered: boolean) => {
    items.forEach((item) => {
      const { testSuiteId, category, subcategory, id: testSpecificationId, type } = item.testSpecification;
      const isCompliance = type === 'compliance';

      if (isCompliance) {
        // Compliance hierarchy: Suite -> Category -> Subcategory -> Test Specification
        const suiteKey = testSuiteId.toString();
        const categoryKey = `${suiteKey}.${category}`;
        const subcategoryKey = `${categoryKey}.${subcategory}`;
        const specKey = `${subcategoryKey}.${testSpecificationId}`;

        incrementCounts(counts, suiteKey, item, isFiltered);
        incrementCounts(counts, categoryKey, item, isFiltered);
        incrementCounts(counts, subcategoryKey, item, isFiltered);
        incrementCounts(counts, specKey, item, isFiltered);
      } else {
        // Non-compliance hierarchy: Test Specification only (flat)
        const specKey = `${testSuiteId}.${testSpecificationId}`;
        incrementCounts(counts, specKey, item, isFiltered);
      }
    });
  };

  processItems(allItems, false);
  processItems(filteredItems, true);

  return counts;
};

const calculateFilterCounts = (
  filteredItems: ReportTestItem[],
  allItems: ReportTestItem[]
): FilterState['counts'] => {
  if (!allItems?.length) {
    return {
      ...defaultValues,
    };
  }

  const spaceCounts = generateNestedCounts(
    filteredItems,
    allItems,
    spaceKeyExtractors
  );

  // Rating hierarchy counts
  const ratingCounts = generateNestedCounts(
    filteredItems,
    allItems,
    ratingKeyExtractors
  );

  // Use custom function for test suite counts to handle both compliance and non-compliance hierarchies
  const testSuiteCounts = generateTestSuiteCounts(
    filteredItems,
    allItems
  );

  const elementCounts = generateNestedCounts(
    filteredItems,
    allItems,
    elementKeyExtractors
  );

  // const exclusionCounts = generateNestedCounts(
  //   filteredItems,
  //   allItems,
  //   exclusionKeyExtractors
  // );

  return {
    tests: calculateTestCounts(allItems),
    filtered: calculateTestCounts(filteredItems),
    space: spaceCounts,
    rating: ratingCounts,
    testSuite: testSuiteCounts,
    element: elementCounts,
    status: countSimpleFilter(filteredItems, allItems, 'status'),
    elementName: countSimpleFilter(filteredItems, allItems, 'element_name'),
    elementId: countSimpleFilter(filteredItems, allItems, 'element_id'),
    excluded: calculateExclusionCounts(filteredItems, allItems),
  };
};

export default calculateFilterCounts;
